import { useEffect, useRef, useState } from "react";
import { MessageSquare, RotateCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { GoogleIcon } from "@/components/GoogleIcon";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const id = (s) => s;

const NUDGE = {
  customer: "Your mobile number stays on your account so our partner can call you. You can verify it by SMS later in Profile.",
  partner: "Works only if your Google account is already linked to your partner account.",
};

const GoogleNudge = ({ onGoogle, t, who }) => (
  <div className="space-y-2 rounded-2xl border-2 border-[#D2E0D8] p-3" data-testid="otp-google-nudge">
    <button type="button" onClick={onGoogle} data-testid="otp-google-btn"
      className="flex h-14 w-full items-center justify-center gap-3 rounded-2xl bg-white text-base font-bold text-[#0D3B2B] shadow-sm ring-1 ring-[#D2E0D8] transition-transform active:scale-[.97]">
      <GoogleIcon /> {t("Continue with Google")}</button>
    <p className="text-center text-xs text-[#4A6358]">{t(NUDGE[who] || NUDGE.customer)}</p>
  </div>
);

export const OtpBox = ({ mobile, purpose, who = "customer", onVerified, onBack, onNotFound, onGoogle, t = id }) => {
  const [code, setCode] = useState("");
  const [to, setTo] = useState("");
  const [wait, setWait] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [down, setDown] = useState(false);
  const sent = useRef(false);
  const body = { mobile, purpose, who };

  const request = async (path) => {
    setErr(""); setDown(false); setBusy(true);
    try { const { data } = await api.post(`/otp/${path}`, body); if (data.to) setTo(data.to); setWait(data.resend_in || 30); setCode(""); }
    catch (e) {
      if (e?.response?.status === 404 && onNotFound) return onNotFound();
      setErr(errMsg(e)); setDown(e?.response?.status === 503); setWait(e?.response?.status === 429 ? 30 : 0);
    }
    finally { setBusy(false); }
  };
  useEffect(() => { if (!sent.current) { sent.current = true; request("send"); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (wait <= 0) return; const tm = setTimeout(() => setWait(wait - 1), 1000); return () => clearTimeout(tm); }, [wait]);

  const verify = async (v = code) => {
    if (v.length !== 4) return setErr(t("Please enter the 4-digit code from the SMS."));
    setErr(""); setBusy(true);
    try { const { data } = await api.post("/otp/verify", { ...body, otp: v }); await onVerified(data.otp_token); }
    catch (e) { setErr(errMsg(e)); setCode(""); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 rounded-2xl bg-white p-4" data-testid="otp-box">
      <p className="flex items-center gap-2 font-display text-lg font-bold text-[#0D3B2B]"><MessageSquare className="h-6 w-6 text-[#2E9E6B]" />{t("Enter the SMS code")}</p>
      <p className="text-sm text-[#4A6358]" data-testid="otp-sent-to">{to ? `${t("We sent a 4-digit code to")} ${to}. ${t("It works for 10 minutes.")}` : busy ? t("Sending code…") : t("Tap 'Resend code' to get a code.")}</p>
      <div className="flex justify-center">
        <InputOTP maxLength={4} value={code} onChange={(v) => { setCode(v.replace(/\D/g, "")); if (v.length === 4 && !busy) verify(v); }}
          inputMode="numeric" autoComplete="one-time-code" disabled={busy} data-testid="otp-input">
          <InputOTPGroup className="gap-3">
            {[0, 1, 2, 3].map((i) => <InputOTPSlot key={i} index={i} className="h-14 w-14 rounded-xl border-2 border-[#D2E0D8] text-2xl font-bold first:rounded-xl last:rounded-xl" />)}
          </InputOTPGroup>
        </InputOTP>
      </div>
      {err && <p role="alert" className="rounded-xl bg-[#FDECE7] p-3 text-sm font-semibold text-[#B4401F]" data-testid="otp-error">{err}</p>}
      {down && onGoogle && <GoogleNudge onGoogle={onGoogle} t={t} who={who} />}
      <button type="button" disabled={busy || code.length !== 4} onClick={() => verify()} data-testid="otp-verify-btn"
        className="flex h-14 w-full items-center justify-center rounded-2xl bg-[#0D3B2B] text-base font-bold text-white transition-transform active:scale-[.97] disabled:opacity-50">
        {busy ? t("Please wait…") : t("Verify code")}</button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onBack} className="font-semibold text-[#4A6358]" data-testid="otp-back-btn">{t("Change number")}</button>
        <button type="button" disabled={wait > 0 || busy} onClick={() => request(to ? "resend" : "send")} data-testid="otp-resend-btn"
          className="flex items-center gap-1 font-semibold text-[#2E9E6B] disabled:text-[#7B968B]">
          <RotateCw className="h-4 w-4" />{wait > 0 ? `${t("Resend code in")} ${wait}s` : t("Resend code")}</button>
      </div>
    </div>
  );
};
